// Adjectives for worker names
const ADJECTIVES = [
  "Creative",
  "Dynamic", 
  "Clever",
  "Swift",
  "Bright",
  "Curious",
  "Friendly",
  "Helpful",
  "Energetic",
  "Reliable",
  "Patient",
  "Quick",
  "Logical",
  "Methodical",
  "Proactive",
  "Thoughtful"
];

// Personalities for worker names
const PERSONALITIES = [
  "Learner",
  "Teacher",
  "Explorer",
  "Solver",
  "Thinker",
  "Analyst",
  "Communicator",
  "Guide",
  "Assistant",
  "Worker",
  "Helper",
  "Ally"
];

/**
 * Generates a job title based on the worker name
 * @param name - The worker name (e.g., "Creative Learner")
 * @returns A job title (e.g., "Creative Learning Specialist")
 */
export function generateJobTitleFromName(name: string): string {
  const [adjective, personality] = name.split(" ");
  
  const jobTitleMap: { [key: string]: string } = {
    "Learner": "Learning Specialist",
    "Teacher": "Instructional Guide", 
    "Explorer": "Research Analyst",
    "Solver": "Problem Resolution Expert",
    "Thinker": "Strategic Consultant",
    "Analyst": "Data Specialist",
    "Communicator": "Communication Expert",
    "Guide": "Process Specialist",
    "Assistant": "Operations Helper",
    "Worker": "Task Coordinator",
    "Helper": "Implementation Specialist",
    "Ally": "Collaboration Partner"
  };
  
  const jobRole = jobTitleMap[personality] || "Specialist";
  return `${adjective} ${jobRole}`;
}

/**
 * Generates a random worker name using adjective + personality combination
 * @returns A random worker name (e.g., "Creative Learner", "Swift Solver")
 */
export function generateRandomWorkerName(): string {
  const randomAdjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const randomPersonality = PERSONALITIES[Math.floor(Math.random() * PERSONALITIES.length)];
  
  return `${randomAdjective} ${randomPersonality}`;
}

/**
 * Gets the total number of possible name combinations
 * @returns The total number of unique name combinations
 */
export function getTotalNameCombinations(): number {
  return ADJECTIVES.length * PERSONALITIES.length;
}

/**
 * Generates a worker name with optional seed for consistent results
 * @param seed - Optional seed for consistent name generation
 * @returns A worker name
 */
export function generateWorkerNameWithSeed(seed?: string): string {
  if (!seed) {
    return generateRandomWorkerName();
  }
  
  // Simple hash function for seed
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use hash to select adjective and personality
  const adjectiveIndex = Math.abs(hash) % ADJECTIVES.length;
  const personalityIndex = Math.abs(hash >> 8) % PERSONALITIES.length;
  
  return `${ADJECTIVES[adjectiveIndex]} ${PERSONALITIES[personalityIndex]}`;
} 